import type { RoundSnapshot, RoundAction } from "../contracts/RoundSnapshot";
import { FeatureTriggerConsistencyError } from "./ValidationErrors";

/**
 * Server may signal a feature in two places:
 * 1) Action: bonus_feature_won  (the "won" moment)
 * 2) Event: feature_enter with featureState (the "enter" moment)
 *
 * This validator optionally checks that they are consistent.
 *
 * IMPORTANT:
 * - This is NOT schema validation.
 * - This is cross-signal / domain consistency.
 * - It should be opt-in because different games/backends may omit one signal.
 */

export type FeatureWonSignal = {
  featureIdentifier: string;
  featureCountAwarded?: string;
};

export type FeatureEnterSignal = {
  featureIdentifier: string;
  featureCountRemaining?: string;
};

export function validateFeatureTriggerConsistencyOrThrow(roundSnapshot: RoundSnapshot): void {
  const won = extractFirstBonusFeatureWon(roundSnapshot);
  const enter = extractFirstFeatureEnter(roundSnapshot);

  // If one is missing, allow (backend styles differ).
  if (!won || !enter) return;

  // Feature id must match
  if (won.featureIdentifier !== enter.featureIdentifier) {
    throw new FeatureTriggerConsistencyError(
      `Feature trigger mismatch: bonus_feature_won.featureIdentifier="${won.featureIdentifier}" ` +
        `but feature_enter.featureState.featureIdentifier="${enter.featureIdentifier}".`
    );
  }

  // If both counts exist, they should match
  const wonCount = won.featureCountAwarded;
  const enterCount = enter.featureCountRemaining;

  if (wonCount && enterCount && wonCount !== enterCount) {
    throw new FeatureTriggerConsistencyError(
      `Feature trigger count mismatch for feature="${won.featureIdentifier}": ` +
        `bonus_feature_won.featureCountAwarded="${wonCount}" but feature_enter.featureCountRemaining="${enterCount}".`
    );
  }
}

/**
 * Extracts the first bonus_feature_won action found in snapshot events.
 * (We keep "first" deterministic and simple for now.)
 */
export function extractFirstBonusFeatureWon(roundSnapshot: RoundSnapshot): FeatureWonSignal | null {
  for (const e of roundSnapshot.orderedEvents) {
    const actions = e.eventContent.orderedActions ?? [];
    for (const a of actions) {
      if (a.actionTypeName !== "bonus_feature_won") continue;

      const featureIdentifier = readStringField(a, "featureIdentifier");
      if (!featureIdentifier) continue;

      const featureCountAwarded = readStringField(a, "featureCountAwarded") ?? undefined;

      return { featureIdentifier, featureCountAwarded };
    }
  }
  return null;
}

/**
 * Extracts the first feature_enter event with featureState.
 */
export function extractFirstFeatureEnter(roundSnapshot: RoundSnapshot): FeatureEnterSignal | null {
  for (const e of roundSnapshot.orderedEvents) {
    if (e.eventTypeName !== "feature_enter") continue;

    const fs = e.eventContent.featureState;
    if (!fs) continue;

    const featureIdentifier = fs.featureIdentifier;
    if (!featureIdentifier) continue;

    return {
      featureIdentifier,
      featureCountRemaining: fs.featureCountRemaining ?? undefined,
    };
  }
  return null;
}

// -------- helpers --------

function readStringField(action: RoundAction, key: string): string | null {
  const payload = action.actionPayload as Record<string, unknown> | null | undefined;
  if (!payload) return null;

  const v = payload[key];
  if (typeof v !== "string") return null;
  if (v.trim().length === 0) return null;
  return v;
}